/**
 * Página de Productos Retail - Versión Inmersiva (Interior Page Style)
 * Diseño Awwwards para páginas interiores: elegante, funcional, con animaciones sutiles
 * 
 * Según estructura.md - Subpágina Productos Retail:
 * - Título: Productos Retail
 * - Subtítulo: Salsas y productos diseñados para acompañar el día a día de los hogares canarios
 * - NO lleva imagen/banner en el hero (a diferencia de Productos o Quiénes Somos)
 * 
 * Grid de Productos Retail por categorías:
 *   1. Ketchup (Cristal): Ketchup, Ketchup Zero, Ketchup Picante
 *   2. Línea "PE" (Plástico): Ketchup, Mostaza Dulce Original, Mostaza Dulce, Barbacoa
 *   3. Línea "PE Alioli / Mayonesa": Mayonesa, Alioli Receta Canaria
 *   4. Tomates en conserva: Entero Pelado, Frito, Triturado, Doble Concentrado
 */

import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection, MagneticButton } from '../components/animations';
import { retailProductsData } from '../data/retailProductsData';

gsap.registerPlugin(ScrollTrigger);

/**
 * Componente de tarjeta de producto con estilo Awwwards
 * @param {Object} props - Propiedades del producto
 */
const ProductCardImmersive = ({ id, name, shortName, formatDisplay, shortDescription, image }) => {
  const displayName = shortName || name;
  const productLink = `/productos/retail/${id}`;

  return (
    <Link to={productLink} className="group block h-full">
      <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col">
        {/* Imagen del producto - más compacta */}
        <div className="relative aspect-[4/3] bg-intercasa-bone flex items-center justify-center overflow-hidden flex-shrink-0">
          {image ? (
            <img 
              src={image} 
              alt={name} 
              className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500 ease-out"
            />
          ) : (
            /* Placeholder elegante */
            <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-intercasa-bone to-white">
              <svg 
                className="w-10 h-10 text-intercasa-red/25" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
          )}
          
          {/* Badge de formato */}
          {formatDisplay && (
            <div className="absolute top-2 right-2 bg-intercasa-red backdrop-blur-sm text-white text-[10px] font-lato font-medium px-2 py-1 rounded-full">
              {formatDisplay}
            </div>
          )}

          {/* Overlay hover */}
          <div className="absolute inset-0 bg-intercasa-red/0 group-hover:bg-intercasa-red/5 transition-colors duration-300" />
        </div>

        {/* Contenido - altura fija */}
        <div className="p-3 md:p-4 flex-1 flex flex-col min-h-[120px]">
          <h3 className="font-baskerville text-sm md:text-base font-bold text-intercasa-dark mb-1 group-hover:text-intercasa-red transition-colors duration-300 min-h-[40px]">
            {displayName}
          </h3>
          
          {shortDescription && (
            <p className="font-lato text-xs text-intercasa-dark/60 leading-relaxed mb-2 flex-1">
              {shortDescription}
            </p>
          )}

          {/* CTA */}
          <div className="flex items-center text-intercasa-red font-lato font-medium text-xs group-hover:translate-x-1 transition-transform duration-300">
            <span>Ver producto</span>
            <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
};

/**
 * Componente de sección de categoría
 * @param {Object} props - Propiedades de la categoría
 */
const CategorySection = ({ title, products, delay = 0 }) => {
  return (
    <section className="mb-12 md:mb-16">
      <AnimatedSection animation="fadeUp" delay={delay}>
        <div className="flex items-center mb-6 md:mb-8">
          <div className="w-8 h-px bg-intercasa-red mr-3" />
          <h2 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl">
            {title}
          </h2>
        </div>
      </AnimatedSection>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
        {products.map((product, index) => (
          <AnimatedSection key={product.id} animation="fadeUp" delay={delay + 0.05 + (index * 0.03)} className="h-full">
            <ProductCardImmersive {...product} />
          </AnimatedSection>
        ))}
      </div>
    </section>
  );
};

/**
 * Página de productos Retail con diseño inmersivo estilo Awwwards
 * @returns {JSX.Element} Página de productos retail inmersiva
 */
const RetailProductsPageImmersive = () => {
  const heroRef = useRef(null);
  const location = useLocation();

  // Scroll al inicio cuando se carga la página
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' },
      '-=0.3'
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO CON BANNER - Imagen de fondo
        Usando banner-rojo.jpg para productos retail
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo rojo base */}
        <div className="absolute inset-0 bg-intercasa-red" />
        
        {/* Imagen en patrón repetido (tile) - más pequeña */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(/images/banners/banner-rojo.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-red/90 via-intercasa-red/70 to-intercasa-red/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-white/60 font-lato text-sm mb-6">
              <Link to="/productos" className="hover:text-white transition-colors">Productos</Link>
              <span>/</span>
              <span className="text-intercasa-yellow">Retail</span>
            </div>

            {/* Título según estructura.md */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-4">
              Productos Retail
            </h1>

            {/* Subtítulo según estructura.md */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl leading-relaxed max-w-2xl">
              Salsas y productos diseñados para acompañar el día a día de los hogares canarios.
            </p>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. GRID DE PRODUCTOS - Eliminar sección intro redundante
        Ir directo a los productos
        =============================================
      */}
      <section className="bg-intercasa-bone py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          
          {/* Categoría 1: Ketchup en Cristal */}
          <CategorySection 
            title="Ketchup en Cristal" 
            products={retailProductsData.ketchupCristal}
            delay={0}
          />

          {/* Categoría 2: Línea PE */}
          <CategorySection 
            title="Línea PE" 
            products={retailProductsData.lineaPE}
            delay={0.1}
          />

          {/* Categoría 3: Alioli y Mayonesa */}
          <CategorySection 
            title="Alioli y Mayonesa" 
            products={retailProductsData.alioliMayonesa}
            delay={0.2}
          />

          {/* Categoría 4: Tomates en Conserva */}
          <CategorySection 
            title="Tomates en Conserva" 
            products={retailProductsData.tomates}
            delay={0.3}
          />

        </div>
      </section>

      {/* 
        =============================================
        4. CTA HACIA PRODUCTOS HORECA
        =============================================
      */}
      <section className="bg-intercasa-dark py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Contenido */}
            <div>
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-yellow text-sm uppercase tracking-widest mb-4 block">
                  Formatos profesionales
                </span>
                <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
                  ¿Buscas productos para{' '}
                  <span className="text-intercasa-yellow">hostelería?</span>
                </h2>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={0.1}>
                <p className="font-lato text-white/70 text-lg leading-relaxed mb-8">
                  Descubre nuestra línea Horeca, diseñada específicamente para hoteles, 
                  restaurantes y servicios de catering. Formatos grandes y presentaciones 
                  profesionales con la misma calidad Intercasa.
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fadeUp" delay={0.2}>
                <Link to="/productos/horeca">
                  <MagneticButton variant="secondary" size="lg">
                    Ver productos Horeca
                  </MagneticButton>
                </Link>
              </AnimatedSection>
            </div>

            {/* Visual decorativo */}
            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden relative">
                  {/* Imagen de productos HORECA */}
                  <img 
                    src="/images/productos/productos-horeca-black.webp" 
                    alt="Productos HORECA - Formatos profesionales"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Badge flotante */}
                <div className="absolute -bottom-4 -left-4 bg-intercasa-yellow text-intercasa-dark px-6 py-3 rounded-xl shadow-lg">
                  <span className="font-baskerville font-bold text-2xl">+20</span>
                  <span className="font-lato text-sm ml-2">productos Horeca</span>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        5. CONTACTO CTA
        =============================================
      */}
      <section className="bg-intercasa-bone py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <h2 className="font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl mb-4">
                ¿Tienes alguna pregunta sobre nuestros productos?
              </h2>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="text-center font-lato text-intercasa-dark/60 text-lg mb-8">
                Estamos aquí para ayudarte a encontrar el producto perfecto para tus necesidades.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <Link to="/contacto">
                <MagneticButton variant="outline" size="md">
                  Contáctanos
                </MagneticButton>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default RetailProductsPageImmersive;
